import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// Use the production entry so the button also shows on the deployed (prod build)
// admin site, not just in `yarn start` dev mode.
import { ReactQueryDevtools } from '@tanstack/react-query-devtools/production';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocaleProvider } from './context/LocaleContext';
import { ApiEnvironmentProvider } from './context/ApiEnvironmentContext';
import { DmPersonaProvider } from './context/DmPersonaContext';
import ProtectedRoute from './layout/ProtectedRoute';
import AppLayout from './layout/AppLayout';
import Login from './page/Login/Login';
import Dashboard from './page/Dashboard/Dashboard';
import Analytics from './page/Analytics/Analytics';
import Ads from './page/Ads/Ads';
import Products from './page/Products/Products';
import ProductPreview from './page/Products/ProductPreview';
import ProductPlazaPreview from './page/Products/PlazaPreview';
import CostScale from './page/CostScale/CostScale';
import Season from './page/Season/Season';
import User from './page/User/User';
import Celeb from './page/Celeb/Celeb';
import CelebBubble from './page/CelebBubble/CelebBubble';
import Report from './page/Report/Report';
import Moderation from './page/Moderation/Moderation';
import BanHistory from './page/BanHistory/BanHistory';
import MobileReleaseCenterSection from './page/MobileReleaseCenter/MobileReleaseCenterSection';
import PolicySettings from './page/PolicySettings/PolicySettings';
import ContentUsage from './page/ContentUsage/ContentUsage';
import ContentCandidates from './page/ContentCandidates/ContentCandidates';
import ServiceAccess from './page/ServiceAccess/ServiceAccess';
import Landmarks from './page/Landmarks/Landmarks';
import ProfileInterestCatalog from './page/ProfileInterestCatalog/ProfileInterestCatalog';
import GameSection from './page/Game/GameSection';
import Community from './page/Community/Community';
import PushSection from './page/Push/PushSection';
import DmSection from './page/Dm/DmSection';
import CelebRoomSection from './page/CelebRoom/CelebRoomSection';
import AdminAccounts from './page/AdminAccounts/AdminAccounts';
import Profile from './page/Profile/Profile';
import Plaza from './page/Plaza/Plaza';
import PlazaPreview from './page/Plaza/PlazaPreview';
import LocalEvents from './page/LocalEvents/LocalEvents';
import SportsPreview from './page/Plaza/Sports/SportsPreview';
import SpawnPoints from './page/SpawnPoints/SpawnPoints';
import { MOBILE_RELEASE_CENTER_LEGACY_ROUTES } from './page/MobileReleaseCenter/mobileReleaseCenterRoutes';
import './App.css';

const SpawnPreview = process.env.NODE_ENV === 'development' ? require('./page/SpawnPoints/SpawnPreview').default : null;
const DmAdminPreview = process.env.NODE_ENV === 'development' ? require('./page/Moderation/DmAdminPreview').default : null;
const LocalEventsPreview = process.env.NODE_ENV === 'development' && process.env.REACT_APP_LOCAL_EVENTS_LOCAL_PREVIEW === 'true' ? require('./page/LocalEvents/LocalEventsPreview').default : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60 * 1000 },
  },
});

// Only mount the React Query Devtools floating button once an admin is logged in.
function AdminDevtools() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return null;
  return <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <LocaleProvider>
          <ApiEnvironmentProvider>
            <AuthProvider>
              <DmPersonaProvider>
                <Routes>
                  {DmAdminPreview && <Route path="/dev/dm-admin" element={<DmAdminPreview />} />}
                  {LocalEventsPreview && <Route path="/__local-events-preview" element={<LocalEventsPreview />} />}
                  <Route path="/login" element={<Login />} />
                  {SpawnPreview && process.env.REACT_APP_SPAWN_LOCAL_PREVIEW === 'true' && <Route path="/__spawn-preview" element={<SpawnPreview />} />}
                  {process.env.NODE_ENV === 'development' && process.env.REACT_APP_PRODUCT_LOCAL_PREVIEW === 'true' && <Route path="/__product-preview" element={<ProductPreview />} />}
                  {process.env.NODE_ENV === 'development' && process.env.REACT_APP_PRODUCT_LOCAL_PREVIEW === 'true' && <Route path="/__product-plaza-preview" element={<ProductPlazaPreview />} />}
                  {process.env.NODE_ENV === 'development' && process.env.REACT_APP_PLAZA_LOCAL_PREVIEW === 'true' && <Route path="/__plaza-preview" element={<PlazaPreview />} />}
                  {process.env.NODE_ENV === 'development' && process.env.REACT_APP_PLAZA_LOCAL_PREVIEW === 'true' && <Route path="/__sports-preview" element={<SportsPreview />} />}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Dashboard />} />
                    <Route path="analytics/*" element={<Analytics />} />
                    <Route path="ads/*" element={<Ads />} />
                    <Route path="products/:productId" element={<Products />} />
                    <Route path="products" element={<Navigate to="/products/story" replace />} />
                    <Route path="cost-scale" element={<CostScale />} />
                    <Route path="season/*" element={<Season />} />
                    <Route path="user" element={<User />} />
                    <Route path="celebs" element={<Celeb />} />
                    <Route path="celebs/bubbles" element={<CelebBubble />} />
                    <Route path="push/*" element={<PushSection />} />
                    <Route path="community/*" element={<Community />} />
                    <Route path="report" element={<Report />} />
                    <Route path="moderation" element={<Moderation />} />
                    <Route path="ban-history" element={<BanHistory />} />
                    <Route path="mobile-release-center/*" element={<MobileReleaseCenterSection />} />
                    <Route path="settings/policies" element={<PolicySettings />} />
                    <Route path="settings/content-usage" element={<ContentUsage />} />
                    <Route path="content-candidates" element={<ContentCandidates />} />
                    <Route path="settings/spawn-points" element={<SpawnPoints />} />
                    <Route path="service-access" element={<ServiceAccess />} />
                    <Route path="landmarks" element={<Landmarks />} />
                    <Route path="profile-interest-catalog" element={<ProfileInterestCatalog />} />
                    <Route path="plazas" element={<Plaza />} />
                    <Route path="local-events" element={<LocalEvents />} />
                    {Object.entries(MOBILE_RELEASE_CENTER_LEGACY_ROUTES).map(([path, target]) => (
                      <Route key={path} path={path.slice(1)} element={<Navigate to={target} replace />} />
                    ))}
                    <Route path="game/*" element={<GameSection />} />
                    <Route path="dm/*" element={<DmSection />} />
                    <Route path="celeb-room/*" element={<CelebRoomSection />} />
                    <Route path="admin-accounts" element={<AdminAccounts />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <AdminDevtools />
              </DmPersonaProvider>
            </AuthProvider>
          </ApiEnvironmentProvider>
        </LocaleProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
